import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft, ShoppingBag, Tag, MessageSquare, Truck, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const steps = [
  {
    icon: Sparkles,
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-50',
    title: 'Welcome to Minted',
    subtitle: 'Your premium marketplace',
    description: 'Buy and sell pre-loved fashion, electronics, homewares and more — all in one beautiful place.',
    image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=600&h=300&fit=crop&q=80',
  },
  {
    icon: ShoppingBag,
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-50',
    title: 'Discover & Buy',
    subtitle: 'Shop with confidence',
    description: 'Browse categories, search for items, and checkout securely with Stripe. Your payments are always protected.',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=300&fit=crop&q=80',
  },
  {
    icon: Tag,
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-50',
    title: 'Sell Your Items',
    subtitle: 'List in under a minute',
    description: 'Snap a photo, set your price, and go live. When it sells, we handle payments and can even generate your shipping label.',
    image: 'https://images.unsplash.com/photo-1556742031-c6961e8560b0?w=600&h=300&fit=crop&q=80',
  },
  {
    icon: MessageSquare,
    iconColor: 'text-purple-600',
    iconBg: 'bg-purple-50',
    title: 'Chat & Negotiate',
    subtitle: 'Direct messaging built in',
    description: 'Message sellers to ask questions or make offers. Everything stays on-platform so you\'re always covered.',
    image: 'https://images.unsplash.com/photo-1577563908411-5077b6dc7624?w=600&h=300&fit=crop&q=80',
  },
  {
    icon: Truck,
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-50',
    title: 'Ship & Track',
    subtitle: 'Real-time order tracking',
    description: 'Get live shipping quotes at checkout, track your orders step-by-step, and confirm delivery when your item arrives.',
    image: 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=600&h=300&fit=crop&q=80',
  },
];

const OnboardingWalkthrough = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [exiting, setExiting] = useState(false);

  const finish = () => {
    setExiting(true);
    localStorage.setItem('minted_onboarding_complete', 'true');
    setTimeout(() => { if (onComplete) onComplete(); }, 300);
  };

  const next = () => {
    if (current < steps.length - 1) setCurrent(current + 1);
    else finish();
  };

  const prev = () => {
    if (current > 0) setCurrent(current - 1);
  };

  const step = steps[current];
  const Icon = step.icon;
  const isLast = current === steps.length - 1;

  return (
    <div
      className={`fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${exiting ? 'opacity-0' : 'opacity-100'}`}
      data-testid="onboarding-overlay"
    >
      <div className={`relative bg-white w-full max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden transition-transform duration-300 ${exiting ? 'translate-y-full sm:scale-95' : 'translate-y-0 sm:scale-100'}`}>
        {/* Hero image */}
        <div className="relative h-44 overflow-hidden">
          <img
            src={step.image}
            alt=""
            className="w-full h-full object-cover transition-all duration-500"
            key={current}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-9 h-9 rounded-xl ${step.iconBg} flex items-center justify-center shadow-sm`}>
                <Icon className={`w-5 h-5 ${step.iconColor}`} />
              </div>
              <div>
                <p className="text-white text-[10px] uppercase tracking-wider font-medium opacity-80">{step.subtitle}</p>
                <h2 className="text-white text-lg font-display leading-tight">{step.title}</h2>
              </div>
            </div>
          </div>
          {/* Close */}
          <button
            onClick={finish}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white transition-colors"
            data-testid="onboarding-skip-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pt-5 pb-4">
          <p className="text-stone-600 text-sm leading-relaxed" data-testid="onboarding-description">
            {step.description}
          </p>
        </div>

        {/* Progress + Nav */}
        <div className="px-6 pb-6">
          {/* Dots */}
          <div className="flex justify-center gap-1.5 mb-5">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === current ? 'w-6 bg-emerald-700' : i < current ? 'w-1.5 bg-emerald-300' : 'w-1.5 bg-stone-200'
                }`}
                data-testid={`onboarding-dot-${i}`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3">
            {current > 0 ? (
              <Button
                variant="outline"
                className="flex-1 h-11 rounded-xl border-stone-200 text-stone-600"
                onClick={prev}
                data-testid="onboarding-prev-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="flex-1 h-11 text-stone-400"
                onClick={finish}
                data-testid="onboarding-skip-text-btn"
              >
                Skip
              </Button>
            )}
            <Button
              className="flex-1 h-11 rounded-xl bg-emerald-800 hover:bg-emerald-700 text-sm"
              onClick={next}
              data-testid="onboarding-next-btn"
            >
              {isLast ? 'Start Shopping' : 'Next'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWalkthrough;
